import { useState } from 'react';
import Swal from 'sweetalert2';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { Modal, ModalBody, ModalStepper } from '@components/ui';
import { FORM_SKIN } from '@components/ui/designTokens';
import { useAuthStore } from '@features/auth/store/authStore';
import { companyService as companyProfileService } from '@features/company/services/companyService';
import { certificateService } from '@features/certificates/services/certificateService';
import { useNewCompanyWizard } from '../../hooks/useNewCompanyWizard';
import { useCreateAddress, useCreateBranchOffice } from '../../hooks/useBranchOffice';
import { useCreateCompany } from '../../hooks/useCreateCompany';
import { Step1CompanyData } from './Step1CompanyData';
import { Step2FiscalAddress } from './Step2FiscalAddress';
import { Step3Certificates } from './Step3Certificates';
import type {
  NewCompanyStep1Data,
  NewCompanyStep2Data,
  NewCompanyStep3Data,
} from '../../types/newCompanyWizard.types';

// GUID fijo usado por BranchOfficeFormModal para el tipo de dirección "Trabajo".
const ADDRESS_TYPE_ID = '42ACBCF4-BE83-4F03-8B5E-5760FC92B59B';
const STEPS = ['Datos de la compañía', 'Dirección fiscal', 'Certificados'];

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) return response.data.message;
  }
  if (error instanceof Error) return error.message;
  return 'Ocurrió un error inesperado';
}

interface NewCompanyWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NewCompanyWizardModal({ isOpen, onClose }: NewCompanyWizardModalProps) {
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.isSuperAdmin === true;

  const {
    currentStep,
    wizardData,
    updateStep1,
    updateStep2,
    updateStep3,
    nextStep,
    previousStep,
    resetWizard,
  } = useNewCompanyWizard();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const createAddressMutation = useCreateAddress();
  const createCompanyMutation = useCreateCompany();
  const createBranchOfficeMutation = useCreateBranchOffice();

  const handleClose = () => {
    if (isSubmitting) return;
    resetWizard();
    onClose();
  };

  const handleStep1Next = (data: NewCompanyStep1Data) => {
    updateStep1(data);
    nextStep();
  };

  const handleStep2Next = (data: NewCompanyStep2Data) => {
    updateStep2(data);
    nextStep();
  };

  const handleFinish = async (step3Data: NewCompanyStep3Data) => {
    updateStep3(step3Data);
    setIsSubmitting(true);

    const { step1, step2 } = wizardData;
    let companyId: string | null = null;

    try {
      // 1. Crear dirección fiscal
      const addressResponse = await createAddressMutation.mutateAsync({
        AddressDetail: step2.addressDetail,
        HomeOrFloorNumber: step2.homeOrFloorNumber,
        CountryId: step2.geolocationData?.CountryId || '',
        ProvinceId: step2.provinceId,
        DistrictId: step2.districtId,
        TownshipId: step2.townshipId,
        Longitude: step2.coordinates!.lng.toString(),
        Latitude: step2.coordinates!.lat.toString(),
        AddressTypeId: ADDRESS_TYPE_ID,
        AddressFiscalCode: step2.fiscalCode,
      });

      if (!addressResponse.AddressId) {
        throw new Error('No se recibió el AddressId de la dirección creada');
      }

      // 2. Crear la compañía con la dirección ya asociada
      const { company, ebillSettingsCreated } = await createCompanyMutation.mutateAsync({
        code: step1.code,
        name: step1.name,
        fullName: step1.fullName,
        dv: step1.dv,
        numberId: step1.numberId,
        typeTaxpayer: step1.typeTaxpayer as '1' | '2',
        phone: step1.phone,
        addressId: addressResponse.AddressId,
        isLimited: isSuperAdmin ? step1.isLimited : undefined,
      });

      companyId = company.id;

      // 3. Crear la sucursal "Casa Matriz" por defecto (mismo patrón que el portal legacy).
      // Code va con 4 dígitos ('0001'): la ficha técnica de la DGI exige ese
      // formato para dSucEm, 2 dígitos lo rechaza.
      await createBranchOfficeMutation.mutateAsync({
        CompanyId: companyId,
        Code: '0001',
        Description: 'Casa Matriz',
        IsActive: true,
        EmailBranchOffice: '',
        AddressId: addressResponse.AddressId,
        addressFiscalCode: step2.fiscalCode,
      });

      // 4. Subir el logo, si se seleccionó uno (requiere companyId ya existente)
      if (step1.logoFile) {
        try {
          await companyProfileService.uploadFile(step1.logoFile, companyId, 'company');
        } catch (logoError) {
          console.error('Error uploading company logo:', logoError);
          // No bloqueante: la compañía puede completarse sin logo y editarse después
        }
      }

      // 5. Subir certificados. El companyId ya quedó seleccionado en la sesión
      // por el BFF al crear la compañía, así que /certificates/upload lo resuelve solo.
      await certificateService.uploadCertificates({
        authCertFile: step3Data.authCertFile!,
        signCertFile: step3Data.signCertFile!,
        secretJwt: step3Data.secretJwt || null,
        certificatePassword: step3Data.certificatePassword,
      });

      if (ebillSettingsCreated === false) {
        await Swal.fire({
          icon: 'warning',
          title: 'Compañía creada con datos pendientes',
          text: 'La compañía se creó, pero no se pudo crear su configuración de facturación (plantillas de correo y CAFE). Contacte a soporte para completarla.',
          confirmButtonColor: '#f59e0b',
        });
      } else {
        await Swal.fire({
          icon: 'success',
          title: '¡Compañía creada!',
          text: 'La compañía se creó exitosamente con su dirección fiscal y certificados.',
          confirmButtonColor: '#10b981',
        });
      }

      resetWizard();
      onClose();
      // Recarga completa: misma estrategia que useCompanies/CompanySelectorPage al
      // cambiar de compañía, para asegurar caché de React Query y sesión frescos.
      window.location.reload();
    } catch (error) {
      console.error('Error creating company:', error);
      const message = getErrorMessage(error);

      if (companyId) {
        // La compañía ya se creó — dejamos que el usuario complete lo que falló
        // desde la pantalla de edición, en vez de perder el progreso.
        await Swal.fire({
          icon: 'warning',
          title: 'Compañía creada con datos pendientes',
          text: `La compañía se creó, pero ocurrió un error al finalizar: ${message}. Puede completar los datos faltantes desde "Editar Compañía".`,
          confirmButtonColor: '#f59e0b',
        });
        resetWizard();
        onClose();
        window.location.reload();
        return;
      }

      await Swal.fire({
        icon: 'error',
        title: 'Error al crear la compañía',
        text: message,
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      kicker={`Alta · Paso ${currentStep + 1} de ${STEPS.length}`}
      title="Nueva Compañía"
      icon={<BuildingOffice2Icon />}
      size="4xl"
      hideBackdrop
      data-testid="company-wizard-modal"
      stepper={
        <ModalStepper
          steps={STEPS}
          current={currentStep}
          testIdBase="company-wizard-step-indicator"
        />
      }
    >
      {/*
        Sin wrapper extra entre ModalBody y el paso: cada Step vive
        directamente en la banda de cuerpo (px-9 py-7 de MODAL_BODY), igual
        que ProductForm en /products — así el pie de cada Step puede sangrar
        hasta el borde del panel con el mismo -mx-9/-mb-7 ya probado ahí, en
        vez de calcular un offset distinto por un wrapper de más.

        `FORM_SKIN` viste los <label>/<input>/<select> de los Input/Select
        compartidos con la tipografía real del portal (12px semibold en
        labels, 13.5px en campos) — el mismo tratamiento que ya usa
        BranchOfficeFormModal, el flujo hermano de este wizard.
      */}
      <ModalBody className={`max-h-[85vh] ${FORM_SKIN}`}>
        {currentStep === 0 && (
          <Step1CompanyData
            data={wizardData.step1}
            isSuperAdmin={isSuperAdmin}
            onNext={handleStep1Next}
          />
        )}

        {currentStep === 1 && (
          <Step2FiscalAddress data={wizardData.step2} onNext={handleStep2Next} onBack={previousStep} />
        )}

        {currentStep === 2 && (
          <Step3Certificates
            data={wizardData.step3}
            isSubmitting={isSubmitting}
            onFinish={handleFinish}
            onBack={previousStep}
          />
        )}
      </ModalBody>
    </Modal>
  );
}
